<script lang="ts">
  /**
   * Outbox Queue — the "Outbox Queue" tab on Posts & Comments. What this owner's
   * Helper is about to send, and what it has already sent, for the selected
   * community. Folded in from the retired standalone /admin/moderation/queue page;
   * mirrors the Helper extension's Outbox screen.
   *
   * ON /v1 — `@api/v1/outbox`, doc 04 §3.3 rows 70/71. `GET /outbox/items` stays
   * mounted forever (the Helper's own Outbox screen reads it); what changed is who
   * the web app asks. Three crossings the code below had to follow:
   *
   *   1. THE ENVELOPE. `{skool_id, items, total, limit, offset, counts, helper}`
   *      became `{items, next_cursor, total}` with `community`, `counts` and
   *      `helper` beside them (§1.2). `limit`/`offset` are request state and are no
   *      longer echoed, so `offset` here is the only record of which page we asked
   *      for; `total` is typed `number | null` because the envelope allows a route
   *      to decline the count — this one always computes it.
   *   2. THE TARGET. Six flat keys became `{kind, id}` plus §2.1's `MemberRef` and
   *      §2.2's `PostRef`, each null on its own when the replied-to row has been
   *      deleted — where the legacy route handed back an all-null object so an
   *      unconditional dereference would not throw. Hence `{@const t = item.target}`
   *      and optional chaining rather than six renamed fields.
   *   3. THE CANCEL. `DELETE …/outbox/items/{id}` answers with the cancelled row AND
   *      the four sidebar counters, so cancelling is ONE request: we patch the row
   *      we already have and hand `badges` to `applyBadges` (§4.2 W2), instead of
   *      re-reading the whole page and leaving the nav counts to drift until the
   *      next navigation.
   */
  import { navigate } from '../router.svelte';
  import StateBlock from '@ui/StateBlock.svelte';
  import { handle401, timeAgo } from '../admin';
  import { applyBadges } from '../todos.svelte';
  import { discardOutboxItem, fetchOutboxItems, type OutboxItem, type OutboxPage, type OutboxStatus } from '@api';

  // Scoped to the active community; the host page hands us the id and bumps
  // `refresh` after it queues a new reply so the list reflects it on switch-over.
  let { skoolId, refresh = 0 }: { skoolId: number | null; refresh?: number } = $props();

  let data = $state<OutboxPage | null>(null);
  let dataLoading = $state(true);
  let error = $state<string | null>(null);
  let loadedId = $state<number | null>(null);
  let statusFilter = $state<OutboxStatus | null>(null);
  const PAGE = 50;
  let offset = $state(0);
  let discarding = $state<Set<number>>(new Set());

  // A ticking clock so the list's relative times ("queued 3m ago") stay fresh.
  // Presence now lives in the shared <HelperPresence> at the page top.
  let nowMs = $state(Date.now());
  $effect(() => {
    const id = setInterval(() => (nowMs = Date.now()), 30_000);
    return () => clearInterval(id);
  });

  const STATUS_META: Record<
    OutboxStatus,
    { label: string; badge: string; dot: string; cancellable: boolean }
  > = {
    queued: { label: 'Queued', badge: 'badge-info', dot: 'bg-info', cancellable: true },
    leased: { label: 'Sending…', badge: 'badge-warning', dot: 'bg-warning', cancellable: false },
    sent: { label: 'Sent', badge: 'badge-success', dot: 'bg-success', cancellable: false },
    failed: { label: 'Failed', badge: 'badge-error', dot: 'bg-error', cancellable: true },
    unknown: { label: 'Checking…', badge: 'badge-warning', dot: 'bg-warning', cancellable: true },
    discarded: { label: 'Cancelled', badge: 'badge-ghost', dot: 'bg-base-content/40', cancellable: false },
  };

  const FILTERS: { key: OutboxStatus; label: string }[] = [
    { key: 'queued', label: 'Queued' },
    { key: 'leased', label: 'Sending' },
    { key: 'unknown', label: 'Checking' },
    { key: 'failed', label: 'Failed' },
    { key: 'sent', label: 'Sent' },
    { key: 'discarded', label: 'Cancelled' },
  ];

  // Two different numbers, deliberately: `counts` is tallied over the WHOLE queue so
  // a chip shows what selecting it would find (and never vanishes when it empties),
  // while `total` is the server's count of rows matching the CURRENT filter and is
  // what the "N–M of T" pager walks.
  const totalCount = $derived(
    data ? Object.values(data.counts).reduce((a, b) => a + b, 0) : 0,
  );
  const filteredTotal = $derived(data?.total ?? 0);

  async function load(
    id: number,
    opts: { resetFilter?: boolean; keepSpinner?: boolean } = {},
  ): Promise<void> {
    dataLoading = !opts.keepSpinner;
    error = null;
    if (opts.resetFilter) {
      statusFilter = null;
      offset = 0;
    }
    try {
      // `status` is a repeated parameter on the wire and takes null for "all", so it
      // is passed unconditionally rather than spread in (§1.6).
      data = await fetchOutboxItems(id, { limit: PAGE, offset, status: statusFilter });
      loadedId = id;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Failed to load the queue.';
      data = null;
    } finally {
      dataLoading = false;
    }
  }

  // Reload on community switch or when the host signals a fresh reply was queued.
  $effect(() => {
    const id = skoolId;
    void refresh;
    if (id == null) return;
    const same = loadedId === id;
    void load(id, { resetFilter: !same, keepSpinner: same });
  });

  function selectFilter(key: OutboxStatus | null): void {
    if (skoolId == null || statusFilter === key) return;
    statusFilter = key;
    offset = 0;
    void load(skoolId);
  }

  async function goToPage(newOffset: number): Promise<void> {
    if (skoolId == null || newOffset < 0) return;
    if (data != null && newOffset >= filteredTotal) return;
    offset = newOffset;
    await load(skoolId);
  }

  async function refreshNow(): Promise<void> {
    if (skoolId != null) await load(skoolId, { keepSpinner: true });
  }

  /**
   * Fold a cancelled row back into the page on screen.
   *
   * The DELETE answers with the row in the same representation the list returns, so
   * there is nothing left to find out: the item flips to "Cancelled", the chip it
   * left loses one and the Cancelled chip gains one. Under a status filter the row
   * no longer matches what is on screen, so it leaves the list and the pager's total
   * with it — which is exactly what the re-read this replaces would have shown.
   */
  function patchCancelled(was: OutboxStatus, row: OutboxItem): void {
    const page = data;
    if (page == null) return;
    const counts = { ...page.counts };
    counts[was] = Math.max(0, counts[was] - 1);
    counts[row.status] += 1;
    const drops = statusFilter != null && statusFilter !== row.status;
    data = {
      ...page,
      counts,
      items: drops
        ? page.items.filter((i) => i.id !== row.id)
        : page.items.map((i) => (i.id === row.id ? row : i)),
      total: drops && page.total != null ? Math.max(0, page.total - 1) : page.total,
    };
  }

  async function cancel(item: OutboxItem): Promise<void> {
    if (skoolId == null) return;
    discarding = new Set(discarding).add(item.id);
    try {
      const { badges, ...row } = await discardOutboxItem(skoolId, item.id);
      // The write moved the Outbox badge; the response carries the new counters, so
      // the sidebar updates without a request of its own.
      applyBadges(skoolId, badges);
      patchCancelled(item.status, row);
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Could not cancel that reply.';
    } finally {
      const next = new Set(discarding);
      next.delete(item.id);
      discarding = next;
    }
  }

  function openThread(item: OutboxItem): void {
    const pid = item.target?.post?.id;
    if (pid != null) navigate(`/admin/community/timeline?post=${pid}`);
  }

  function primaryTime(item: OutboxItem): string | null {
    if (item.status === 'sent') return item.sent_at ?? null;
    if (item.status === 'leased') return item.leased_at ?? null;
    return item.queued_at ?? item.created_at ?? null;
  }
  function primaryTimeLabel(item: OutboxItem): string {
    if (item.status === 'sent') return 'sent';
    if (item.status === 'leased') return 'picked up';
    if (item.status === 'discarded') return 'cancelled';
    return 'queued';
  }
</script>

<div class="mt-5">
  <div class="flex justify-end">
    <button
      class="btn btn-ghost btn-xs shrink-0 gap-1.5 text-base-content/70"
      onclick={refreshNow}
      disabled={dataLoading || skoolId == null}
      title="Refresh"
    >
      <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>
      Refresh
    </button>
  </div>

  <!-- Status filter chips -->
  <div class="mt-5 flex flex-wrap gap-2">
    <button
      class="rounded-full border px-3 py-1 text-xs font-medium transition-colors
        {statusFilter === null
          ? 'border-primary/40 bg-primary/10 text-primary'
          : 'border-base-content/15 text-base-content/70 hover:bg-base-200/60'}"
      onclick={() => selectFilter(null)}
    >
      All <span class="tabular-nums opacity-70">{totalCount}</span>
    </button>
    {#each FILTERS as f (f.key)}
      {@const n = data?.counts[f.key] ?? 0}
      <button
        class="rounded-full border px-3 py-1 text-xs font-medium transition-colors
          {statusFilter === f.key
            ? 'border-primary/40 bg-primary/10 text-primary'
            : 'border-base-content/15 text-base-content/70 hover:bg-base-200/60'}
          {n === 0 && statusFilter !== f.key ? 'opacity-45' : ''}"
        onclick={() => selectFilter(f.key)}
      >
        {f.label} <span class="tabular-nums opacity-70">{n}</span>
      </button>
    {/each}
  </div>

  {#if error && data}
    <div class="mt-4 rounded-field border border-error/30 bg-error/10 px-3.5 py-2 text-sm text-error">{error}</div>
  {/if}

  <!-- The list -->
  <div class="mt-4">
    {#if dataLoading}
      <StateBlock state="loading" compact />
    {:else if error && !data}
      <StateBlock state="error" message={error} onretry={() => skoolId != null && load(skoolId)} />
    {:else if data && data.items.length === 0}
      <StateBlock
        state="empty"
        message={statusFilter ? `No ${STATUS_META[statusFilter].label.toLowerCase()} replies` : 'No replies yet'}
        hint={statusFilter
          ? 'Try a different status.'
          : 'Reply to a post or comment from Posts & Comments and it will appear here, on its way to Skool.'}
      />
    {:else if data}
      <ul class="space-y-2.5">
        {#each data.items as item (item.id)}
          {@const meta = STATUS_META[item.status]}
          {@const t = item.target}
          <li class="rounded-box border border-base-content/10 bg-base-100 p-4 transition-colors hover:border-base-content/20">
            <div class="flex items-start justify-between gap-3">
              <div class="flex min-w-0 items-center gap-2">
                <span class="badge badge-sm {meta?.badge ?? 'badge-ghost'} gap-1.5 whitespace-nowrap">
                  <span class="inline-block h-1.5 w-1.5 rounded-full {meta?.dot ?? 'bg-base-content/40'}"></span>
                  {meta?.label ?? item.status}
                </span>
                <span class="truncate text-xs text-base-content/55">
                  {primaryTimeLabel(item)} {timeAgo(primaryTime(item), nowMs)}
                  {#if (item.attempts ?? 0) > 1}· attempt {item.attempts}{/if}
                </span>
              </div>
              {#if meta?.cancellable}
                <button
                  class="shrink-0 text-xs font-medium text-base-content/55 transition-colors hover:text-error disabled:opacity-50"
                  onclick={() => cancel(item)}
                  disabled={discarding.has(item.id)}
                >
                  {discarding.has(item.id) ? 'Cancelling…' : 'Cancel'}
                </button>
              {/if}
            </div>

            <!-- The reply text we'll post -->
            <p class="redact mt-2.5 whitespace-pre-wrap break-words text-sm text-base-content/90">{item.body ?? ''}</p>

            <!-- What it's replying to, with links out -->
            <div class="mt-2.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-base-content/60">
              <span class="text-base-content/45">Replying to</span>
              {#if t?.author?.member_id != null}
                <button
                  class="redact font-medium text-base-content/80 transition-colors hover:text-primary"
                  onclick={() => navigate(`/admin/community/members/${t?.author?.member_id}`)}
                >{t.author.name ?? 'a member'}</button>
              {:else}
                <span class="redact font-medium text-base-content/80">{t?.author?.name ?? 'a member'}</span>
              {/if}
              <span class="text-base-content/45">on</span>
              {#if t?.post?.id != null}
                <button class="truncate font-medium text-base-content/80 transition-colors hover:text-primary" onclick={() => openThread(item)}>
                  <span class="redact">{t.post.title || 'this thread'}</span>
                </button>
              {:else}
                <span class="redact truncate font-medium text-base-content/80">{t?.post?.title || 'this thread'}</span>
              {/if}
              {#if t?.url}
                <span class="text-base-content/30">·</span>
                <a href={t.url} target="_blank" rel="noopener" class="inline-flex items-center gap-1 text-base-content/60 transition-colors hover:text-primary">
                  View on Skool
                  <svg class="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
                </a>
              {/if}
            </div>

            {#if t?.excerpt}
              <div class="mt-2 border-l-2 border-base-content/15 pl-2.5 text-xs italic text-base-content/55">
                <span class="redact">{t.excerpt}</span>
              </div>
            {/if}

            {#if item.status === 'failed' && item.last_error}
              <div class="mt-2.5 rounded-field bg-error/10 px-2.5 py-1.5 text-xs text-error">{item.last_error}</div>
            {:else if item.status === 'unknown'}
              <div class="mt-2.5 rounded-field bg-warning/10 px-2.5 py-1.5 text-xs text-warning-content/90">
                Send couldn't be confirmed — we're checking Skool and will mark it sent, or re-queue it, automatically.
              </div>
            {:else if item.status === 'sent' && item.external_result_id}
              <div class="mt-2 text-xs text-success/80">Delivered to Skool ✓</div>
            {/if}
          </li>
        {/each}
      </ul>

      {#if filteredTotal > PAGE}
        <div class="mt-5 flex items-center justify-between text-sm">
          <button class="btn btn-sm btn-ghost" onclick={() => goToPage(offset - PAGE)} disabled={offset === 0 || dataLoading}>← Newer</button>
          <span class="font-mono text-xs tabular-nums text-base-content/60">
            {offset + 1}–{Math.min(offset + PAGE, filteredTotal)} of {filteredTotal}
          </span>
          <button class="btn btn-sm btn-ghost" onclick={() => goToPage(offset + PAGE)} disabled={offset + PAGE >= filteredTotal || dataLoading}>Older →</button>
        </div>
      {/if}
    {/if}
  </div>
</div>
